// ─── THE RELATIONSHIPS MENU ─────────────────────────────────────────────────
//
// ONE menu for relationships. Haley, 2026-08-02: "please build unified
// relationship feature tonight and purge the old ones because we want it to be
// one menu".
//
// This replaces three separate controls:
//   • DynamicsEditor — one pair at a time, global only, no typed kind
//   • SixGuysRP charTags — love/lover/friend/enemy, per story, RP only
//   • StoryPreferences relationshipStage — an enum, DM conversations only
//
// What it does that none of them did:
//   • the WHOLE CAST at once, not one pair at a time
//   • a typed relationship kind every prompt path can read
//   • BULK edit — select many characters, set them all in one action
//   • PER-CHAT scope, with an explicit "this chat only" toggle
//   • romantic_contested, so a route can have more than one love interest
//   • an explicit Save button, editable at any time

import { useState, useEffect, useCallback, useMemo } from 'react';
import { CHARACTERS } from './characters.js';
import {
  RELATIONSHIP_KINDS, KIND_BY_VALUE, isRomanticKind,
  loadCastRelationships, saveRelationships, clearScopedRelationship, buildCastRows,
} from './relationshipDynamics.js';

const T = {
  bg: '#1a1a2e', card: '#2f3136', text: '#dcddde', dim: '#a0a0a0',
  accent: '#d1b3ff', border: '#40444b', ok: '#43b581', warn: '#faa61a', danger: '#f04747',
};

const DETAIL_FIELDS = [
  ['behavioral_summary', 'Behavioral Summary', 'How they behave toward this person. What changes in them? What do they hide or show?'],
  ['emotional_patterns', 'Emotional Patterns', 'e.g. "Teasing → tender when she needs reassurance."'],
  ['texting_style', 'Texting Style', 'Pet names, emoji habits, message length, humor'],
  ['vocabulary_notes', 'Vocabulary & Language', 'Words, phrases, slang they use with this person'],
  ['relationship_stage', 'Relationship Notes', 'Free notes — where things stand right now'],
  ['never_do', 'Never Do (Hard Rules)', 'Things they would NEVER do or say to this person'],
];

/**
 * @param {string}   targetCharacterId  who the cast has a relationship WITH (usually the player's character)
 * @param {string[]} castIds            the story/channel cast to show
 * @param {object}   scope              { type: 'dm'|'channel'|'story'|'group', id } — the chat this is for
 * @param {string}   scopeLabel         human name of that chat, for the toggle
 * @param {string}   userId
 */
export default function RelationshipsPanel({
  targetCharacterId: targetProp = null, castIds = null, scope = null,
  scopeLabel = 'this chat', userId = null, onClose,
}) {
  // Opened from a chat, the target and cast are fixed. Opened from the character
  // editor there is no chat, so the panel picks them itself and edits the
  // GLOBAL defaults — same menu, both ways in.
  const [targetCharacterId, setTargetCharacterId] = useState(targetProp);
  // Nicole, 2026-08-10: "Relationship section does not have search bar to
  // search up chars instead of scrolling." The cast list is the whole roster.
  const [castSearch, setCastSearch] = useState('');
  useEffect(() => { setTargetCharacterId(targetProp); }, [targetProp]);
  // Escape closes, like every other modal in the app.
  useEffect(() => {
    if (!onClose) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const effectiveCast = useMemo(
    () => (castIds && castIds.length
      ? castIds
      : CHARACTERS.filter(c => c?.id && c?.name).map(c => c.id)),
    [castIds]);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [expanded, setExpanded] = useState(null);
  const [thisChatOnly, setThisChatOnly] = useState(!!scope?.id);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState('');

  const scopeType = thisChatOnly ? scope?.type : null;
  const scopeId = thisChatOnly ? scope?.id : null;

  const load = useCallback(async () => {
    setLoading(true);
    const existing = await loadCastRelationships(targetCharacterId, { scopeType, scopeId, userId });
    setRows(buildCastRows(effectiveCast, targetCharacterId, existing));
    setSelected(new Set());
    setDirty(false);
    setLoading(false);
  }, [targetCharacterId, effectiveCast, scopeType, scopeId, userId]);

  useEffect(() => { if (targetCharacterId) load(); }, [load, targetCharacterId]);

  const patch = (id, changes) => {
    setRows(rs => rs.map(r => (r.character_id === id ? { ...r, ...changes } : r)));
    setDirty(true);
  };

  // BULK — Haley: "multiple characters at once".
  const applyKindToSelected = (kind) => {
    if (!selected.size) return;
    setRows(rs => rs.map(r => (selected.has(r.character_id) ? { ...r, relationship_kind: kind } : r)));
    setDirty(true);
    setStatus(`Set ${selected.size} character${selected.size === 1 ? '' : 's'} to ${KIND_BY_VALUE[kind]?.label}. Not saved yet.`);
  };

  const toggle = (id) => setSelected(s => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const save = async () => {
    setSaving(true);
    setStatus('');
    // Only send rows that actually say something — an untouched cast member
    // with no kind and no prose should not create a row.
    const meaningful = rows.filter(r =>
      r.relationship_kind || DETAIL_FIELDS.some(([k]) => (r[k] || '').trim()));
    const { saved, error } = await saveRelationships(meaningful, { scopeType, scopeId, userId });
    setSaving(false);
    if (error) { setStatus(`Save failed: ${error}`); return; }
    setDirty(false);
    setStatus(`Saved ${saved} relationship${saved === 1 ? '' : 's'}${thisChatOnly ? ` for ${scopeLabel}` : ' as the default'}.`);
    load();
  };

  const revertToDefault = async (id) => {
    if (!scope?.id) return;
    await clearScopedRelationship(id, targetCharacterId, { scopeType: scope.type, scopeId: scope.id });
    setStatus('Reverted to the default for that character.');
    load();
  };

  const targetName = useMemo(
    () => CHARACTERS.find(c => c.id === targetCharacterId)?.name || 'them', [targetCharacterId]);

  const counts = useMemo(() => {
    const c = {};
    for (const r of rows) if (r.relationship_kind) c[r.relationship_kind] = (c[r.relationship_kind] || 0) + 1;
    return c;
  }, [rows]);


  // ── RENDERED AS A REAL MODAL ────────────────────────────────────────────
  // Nicole, 2026-08-10: "Dynamics section broken in DM - it pulls up dynamics
  // text over rp with transparent background so all the text overlaps. Unsure
  // if there a close menu option due to this."
  //
  // Both DMView and GroupChatView mounted this INLINE in their component tree.
  // The root had a background colour but no position, no z-index and no
  // backdrop, so it laid out in the document flow interleaved with the
  // conversation — which reads exactly as "transparent, all the text
  // overlaps", and buried the Close button that was there all along.
  //
  // Owning the overlay here fixes both surfaces at once and means the next
  // place that mounts it cannot reintroduce the bug. Backdrop click closes,
  // Escape closes, and the header with Close is sticky so it is always
  // reachable however far the cast list scrolls.
  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 4000,
        background: 'rgba(0,0,0,0.62)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
    <div onClick={(e) => e.stopPropagation()} style={{ background: T.bg, color: T.text, padding: 16, borderRadius: 10, maxHeight: '82vh', overflowY: 'auto', width: 'min(720px, 100%)', boxShadow: '0 18px 60px rgba(0,0,0,0.55)', border: `1px solid ${T.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6, position: 'sticky', top: 0, background: T.bg, paddingBottom: 8, zIndex: 1 }}>
        <h3 style={{ margin: 0, color: T.accent, flex: 1 }}>Relationships with {targetName}</h3>
        {onClose && (
          <button onClick={onClose} style={btn(T.border)}>Close</button>
        )}
      </div>
      <p style={{ margin: '0 0 12px', color: T.dim, fontSize: 13 }}>
        How every character in this cast relates to {targetName}. This drives how the AI plays them —
        an enemy stays hostile, a friend stays a friend, and romance only happens where you say it does.
      </p>

      {/* ALWAYS rendered. This used to be {!targetProp && ...}, so opening the
          panel from a chat — which always passes a target — HID the picker and
          locked the direction to one character. Nicole, 2026-08-10:
          "relationship tab only loads up one character (I.e Moros to Clothos
          but can't do reverse for Clothos to Moros)". The passed target is
          still the default; it is just no longer a cage. */}
      {(
        <label style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
          Relationships toward:{' '}
          <select value={targetCharacterId || ''} onChange={e => setTargetCharacterId(e.target.value || null)}
            style={{ ...input, width: 'auto' }}>
            <option value="">— choose a character —</option>
            {CHARACTERS.filter(c => c?.id && c?.name)
              .slice().sort((a, b) => a.name.localeCompare(b.name))
              .map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
      )}

      {!targetCharacterId ? (
        <div style={{ color: T.dim, padding: 20 }}>Choose who the cast relates to.</div>
      ) : (<>

      {/* PER-CHAT SCOPE */}
      {scope?.id && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 13 }}>
          <input type="checkbox" checked={thisChatOnly} onChange={e => setThisChatOnly(e.target.checked)} />
          <span>
            <b>Just for {scopeLabel}</b>
            <span style={{ color: T.dim }}>
              {thisChatOnly
                ? ' — changes here apply only to this chat.'
                : ' — off: editing the default used everywhere this cast appears.'}
            </span>
          </span>
        </label>
      )}

      {/* BULK BAR */}
      <div style={{
        display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
        background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: 10, marginBottom: 12,
      }}>
        <span style={{ fontSize: 13, color: selected.size ? T.text : T.dim }}>
          {selected.size ? `${selected.size} selected` : 'Select characters to set several at once'}
        </span>
        <button onClick={() => setSelected(new Set(rows.map(r => r.character_id)))} style={btn(T.border)}>All</button>
        <button onClick={() => setSelected(new Set())} style={btn(T.border)}>None</button>
        <select
          disabled={!selected.size}
          value=""
          onChange={e => e.target.value && applyKindToSelected(e.target.value)}
          style={{ ...input, width: 'auto', opacity: selected.size ? 1 : 0.5 }}
        >
          <option value="">Set selected to…</option>
          {RELATIONSHIP_KINDS.map(k => (
            <option key={k.value} value={k.value}>{k.emoji} {k.label}</option>
          ))}
        </select>
      </div>

      <input
        value={castSearch}
        onChange={e => setCastSearch(e.target.value)}
        placeholder="Search characters…"
        aria-label="Search characters"
        style={{ ...input, marginBottom: 10 }}
      />

      {loading ? (
        <div style={{ color: T.dim, padding: 20 }}>Loading cast…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: T.dim, padding: 20 }}>No other characters in this cast yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.filter(r => {
            const q = castSearch.trim().toLowerCase();
            if (!q) return true;
            const name = (CHARACTERS.find(c => c.id === r.character_id)?.name || r.character_id || '');
            return name.toLowerCase().includes(q);
          }).map(r => {
            const kind = KIND_BY_VALUE[r.relationship_kind];
            const open = expanded === r.character_id;
            return (
              <div key={r.character_id} style={{
                background: T.card, border: `1px solid ${selected.has(r.character_id) ? T.accent : T.border}`,
                borderRadius: 8, padding: 10,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <input type="checkbox" checked={selected.has(r.character_id)} onChange={() => toggle(r.character_id)} />
                  <b style={{ minWidth: 130 }}>{r.name}</b>

                  <select
                    value={r.relationship_kind || ''}
                    onChange={e => patch(r.character_id, { relationship_kind: e.target.value || null })}
                    style={{ ...input, width: 'auto' }}
                  >
                    <option value="">— not set (no romance assumed) —</option>
                    {RELATIONSHIP_KINDS.map(k => (
                      <option key={k.value} value={k.value}>{k.emoji} {k.label}</option>
                    ))}
                  </select>

                  {r.scoped && thisChatOnly && (
                    <span title="Overrides the default for this chat"
                      style={{ fontSize: 11, color: T.warn, border: `1px solid ${T.warn}`, borderRadius: 5, padding: '1px 5px' }}>
                      this chat
                    </span>
                  )}

                  <button onClick={() => setExpanded(open ? null : r.character_id)} style={{ ...btn(T.border), marginLeft: 'auto' }}>
                    {open ? 'Hide detail' : 'Detail'}
                  </button>
                  {r.scoped && thisChatOnly && (
                    <button onClick={() => revertToDefault(r.character_id)} style={btn(T.border)}>Use default</button>
                  )}
                </div>

                {kind && <div style={{ fontSize: 12, color: T.dim, marginTop: 6, marginLeft: 28 }}>{kind.blurb}</div>}

                {/* MULTIPLE LOVE INTERESTS — name the rivals, or the model forgets they exist */}
                {r.relationship_kind === 'romantic_contested' && (
                  <div style={{ marginTop: 8, marginLeft: 28 }}>
                    <div style={{ fontSize: 12, color: T.warn, marginBottom: 4 }}>
                      Who else is in the running? They stay real in the scene instead of being quietly forgotten.
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {rows.filter(o => o.character_id !== r.character_id && isRomanticKind(o.relationship_kind)).map(o => {
                        const on = (r.contested_with || []).includes(o.character_id);
                        return (
                          <button key={o.character_id}
                            onClick={() => patch(r.character_id, {
                              contested_with: on
                                ? (r.contested_with || []).filter(x => x !== o.character_id)
                                : [...(r.contested_with || []), o.character_id],
                            })}
                            style={{ ...btn(on ? T.accent : T.border), color: on ? T.bg : T.text, background: on ? T.accent : 'transparent' }}>
                            {o.name}
                          </button>
                        );
                      })}
                      {rows.filter(o => o.character_id !== r.character_id && isRomanticKind(o.relationship_kind)).length === 0 && (
                        <span style={{ fontSize: 12, color: T.dim }}>Mark another character romantic first.</span>
                      )}
                    </div>
                  </div>
                )}

                {open && (
                  <div style={{ marginTop: 10, marginLeft: 28, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {DETAIL_FIELDS.map(([key, label, ph]) => (
                      <label key={key} style={{ fontSize: 12, color: T.dim }}>
                        {label}
                        <textarea
                          value={r[key] || ''}
                          placeholder={ph}
                          rows={key === 'behavioral_summary' ? 3 : 2}
                          onChange={e => patch(r.character_id, { [key]: e.target.value })}
                          style={{ ...input, marginTop: 3, resize: 'vertical' }}
                        />
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      </>)}

      {/* SAVE — Haley: "it should save and persist when set with a save button" */}
      <div style={{
        position: 'sticky', bottom: 0, background: T.bg, paddingTop: 12, marginTop: 12,
        borderTop: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      }}>
        <button onClick={save} disabled={saving || !dirty}
          style={{ ...btn(T.ok), background: dirty ? T.ok : 'transparent', color: dirty ? T.bg : T.dim, fontWeight: 700 }}>
          {saving ? 'Saving…' : dirty ? 'Save relationships' : 'Saved'}
        </button>
        {dirty && <span style={{ color: T.warn, fontSize: 12 }}>Unsaved changes</span>}
        <span style={{ fontSize: 12, color: T.dim }}>
          {Object.entries(counts).map(([k, n]) => `${KIND_BY_VALUE[k]?.emoji || ''} ${n}`).join('  ') || 'none set'}
        </span>
        {status && <span style={{ fontSize: 12, color: T.ok, marginLeft: 'auto' }}>{status}</span>}
      </div>
    </div>
    </div>
  );
}

const btn = (color) => ({
  background: 'transparent', color: T.text, border: `1px solid ${color}`,
  borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12,
});
const input = {
  width: '100%', background: T.bg, color: T.text, border: `1px solid ${T.border}`,
  borderRadius: 6, padding: '5px 8px', fontSize: 13, fontFamily: 'inherit',
};
